/**
 * Hook for managing country data (descriptions and details)
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api-client';

export interface CountryData {
  country_code: string;
  description: string | null;
  capital: string | null;
  population: string | null;
  currency: string | null;
  languages: string | null;
  area: string | null;
  // Comprehensive fields
  gdp: string | null;
  hdi: string | null;
  air_quality_avg: string | null;
  crime_avg: string | null;
  weather_winter: string | null;
  weather_spring: string | null;
  weather_summer: string | null;
  weather_autumn: string | null;
  timezone: string | null;
  positives: string | null; // JSON array
  negatives: string | null; // JSON array
  famous_for: string | null;
  created_at: string;
  updated_at: string;
}

export interface CountryDataInput {
  description?: string;
  capital?: string;
  population?: string;
  currency?: string;
  languages?: string;
  area?: string;
  gdp?: string;
  hdi?: string;
  air_quality_avg?: string;
  crime_avg?: string;
  weather_winter?: string;
  weather_spring?: string;
  weather_summer?: string;
  weather_autumn?: string;
  timezone?: string;
  positives?: string; // JSON array as string
  negatives?: string; // JSON array as string
  famous_for?: string;
}

export function useCountryData() {
  const [data, setData] = useState<Record<string, CountryData>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<{ data: Record<string, CountryData> }>(
        '/api/country-data?grouped=true'
      );
      setData(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch country data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const getCountryData = useCallback(
    async (countryCode: string): Promise<CountryData | null> => {
      const result = await api.get<{ data: CountryData | null }>(
        `/api/country-data/${countryCode}`
      );
      return result.data;
    },
    []
  );

  const saveCountryData = useCallback(
    async (countryCode: string, input: CountryDataInput): Promise<CountryData> => {
      const result = await api.put<{ data: CountryData }>(
        `/api/country-data/${countryCode}`,
        input
      );
      // Update local state
      setData((prev) => ({
        ...prev,
        [countryCode]: result.data,
      }));
      return result.data;
    },
    []
  );

  return {
    data,
    loading,
    error,
    refetch: fetchAllData,
    getCountryData,
    saveCountryData,
  };
}
